import type { Appearance } from '@/api/generated/schemas.ts';
import { readStored, writeStored } from '@/utils/storage.ts';

import { DEFAULT_APPEARANCE, FONTS, LAYOUTS, THEMES, type Tokens, tokenName } from './themes.ts';

// What this browser last showed, so a reload shows it again before the account is loaded rather than flashing the
// app's own look.
const STORAGE_KEY = 'appearance';

const mix = (color: string, percent: number, base: string) => `color-mix(in srgb, ${color} ${percent}%, ${base})`;

// A theme in a layout with someone's own colours over it, for the light or the dark theme; what has no colour of its
// own (a hover, the lightest gray text) follows the own colour it belongs to.
export const appearanceTokens = (appearance: Appearance, isDark: boolean): Tokens => {
    const theme = THEMES[appearance.theme] ?? THEMES.classic;
    const layout = LAYOUTS[appearance.layout] ?? LAYOUTS.flat;
    const base = isDark ? theme.dark : theme.light;
    const tokens = { ...base, ...layout.tokens(base, isDark) };
    const own = Object.fromEntries(
        Object.entries(isDark ? appearance.colors_dark : appearance.colors_light).map(([name, value]) => [
            tokenName(name),
            value,
        ])
    );
    return {
        ...tokens,
        ...own,
        ...(own.primary && { 'primary-hover': mix(isDark ? '#ffffff' : '#000000', 14, own.primary) }),
        ...(own['surface-muted'] && {
            'surface-hover': mix(own.text ?? tokens.text, 6, own['surface-muted']),
            'surface-sunken': own['surface-muted'],
        }),
        ...(own['text-muted'] && { 'text-subtle': mix(own['text-muted'], 55, own.surface ?? tokens.surface) }),
    };
};

export const isDefaultAppearance = (appearance: Appearance | null | undefined) =>
    !appearance ||
    (appearance.theme === DEFAULT_APPEARANCE.theme &&
        appearance.layout === DEFAULT_APPEARANCE.layout &&
        appearance.headline_font === DEFAULT_APPEARANCE.headline_font &&
        Object.keys(appearance.colors_light).length === 0 &&
        Object.keys(appearance.colors_dark).length === 0 &&
        appearance.is_dark_derivation_enabled !== false);

const clearTokens = () => {
    const root = document.documentElement;
    for (const name of [...root.style].filter((name) => name.startsWith('--color-') || name === '--font-headline')) {
        root.style.removeProperty(name);
    }
};

// Sets the appearance's tokens on <html>, over index.css's, and keeps it for the next reload; null or today's look
// leaves index.css's as they are.
export const applyAppearance = (appearance: Appearance | null | undefined, isDark: boolean) => {
    writeStored(STORAGE_KEY, isDefaultAppearance(appearance) ? null : JSON.stringify(appearance));
    showAppearance(appearance, isDark);
};

// The appearance as the CSS variables that set it, also for the prerendered start page (landingStyle).
export const appearanceVariables = (appearance: Appearance, isDark: boolean): Record<string, string> => ({
    ...Object.fromEntries(
        Object.entries(appearanceTokens(appearance, isDark)).map(([name, value]) => [`--color-${name}`, value])
    ),
    '--font-headline': (FONTS[appearance.headline_font] ?? FONTS.inter).family,
});

// Sets the appearance's tokens without keeping them, e.g. the start page's own look (LANDING_APPEARANCE), which isn't
// the visitor's.
export const showAppearance = (appearance: Appearance | null | undefined, isDark: boolean) => {
    clearTokens();
    if (!appearance || isDefaultAppearance(appearance)) return;
    for (const [name, value] of Object.entries(appearanceVariables(appearance, isDark))) {
        document.documentElement.style.setProperty(name, value);
    }
    void (FONTS[appearance.headline_font] ?? FONTS.inter).load?.();
};

// Leaves the app's own look, e.g. on the start page, without forgetting the appearance for the next reload.
export const removeAppearance = () => clearTokens();

export const readStoredAppearance = (): Appearance | null => {
    const stored = readStored(STORAGE_KEY);
    if (!stored) return null;
    try {
        return JSON.parse(stored) as Appearance;
    } catch {
        return null;
    }
};

// The start page, its legal pages and the changelog in a look of their own for everyone, signed in or not: they're
// Recap's public face, not anyone's workspace. Klassisch's black with Terrakotta's warm grays.
export const LANDING_APPEARANCE: Appearance = {
    ...DEFAULT_APPEARANCE,
    layout: 'tinted_rail',
    colors_light: {
        surface: '#ffffff',
        surface_shaded: '#f9f7f3',
        surface_muted: '#f6f3ed',
        border: '#ebebeb',
        text: '#0a0a0a',
        text_muted: '#78716a',
        primary: '#0a0a0a',
        on_primary: '#ffffff',
        rail: '#faf8f3',
        top: '#fefdfb',
        nav_hover: '#f4f0e8',
        nav_active: '#efeae1',
        nav_active_text: '#1a1714',
        today: '#0a0a0a',
        today_text: '#ffffff',
        eyebrow: '#bf8464',
        tab_line: '#0a0a0a',
        bar_current: '#0a0a0a',
        link: '#0a0a0a',
    },
    colors_dark: {
        surface: '#1a1918',
        surface_shaded: '#22201c',
        surface_muted: '#262420',
        border: '#262626',
        text: '#f2f2f2',
        text_muted: '#9c968a',
        primary: '#f2f2f2',
        on_primary: '#0a0a0a',
        rail: '#201d1a',
        top: '#1c1a17',
        nav_hover: '#282521',
        nav_active: '#2d2823',
        nav_active_text: '#f0ece3',
        today: '#f2f2f2',
        today_text: '#151515',
        eyebrow: '#c48a6a',
        tab_line: '#f2f2f2',
        bar_current: '#f2f2f2',
        link: '#f2f2f2',
    },
};

// Before the first frame of the app (index.tsx), what this browser last showed; the account's replaces it once it's
// loaded (useAppearance). Only in a workspace: the sign-in keeps the app's own look, the start page its own
// (LANDING_APPEARANCE).
export const restoreAppearance = (isDark: boolean) => {
    if (!window.location.pathname.startsWith('/w/')) return;
    applyAppearance(readStoredAppearance(), isDark);
};
